import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { forField, type FieldSpec, type Problem } from "../../lib/master";

/**
 * The inputs the master-data forms are built from (D-142).
 *
 * One component per field *kind* rather than one per field: the four forms are field lists in `lib/master.ts`, so a
 * field added there appears here without a line of screen code. Problems render underneath the input they belong
 * to, worst first, from the code the rules return (`master.problem.<code>`) — the same codes the endpoint uses, so
 * a rule reads correctly in French and Lingala without a new endpoint response.
 */

export function Section({ title, hint, children, hook }: { title: string; hint?: string; children: ReactNode; hook?: string }) {
  return (
    <section className="rounded-lg border border-brand-grey-light bg-white p-4" data-master-section={hook}>
      <h2 className="font-heading text-base font-semibold">{title}</h2>
      {hint && <p className="mt-0.5 text-[11px] text-brand-grey">{hint}</p>}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

function Problems({ problems }: { problems: Problem[] }) {
  const { t } = useTranslation();
  if (problems.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {problems.map((p) => (
        <p
          key={p.code}
          data-master-problem={p.code}
          data-master-problem-severity={p.severity}
          className={`text-[11px] ${p.severity === "error" ? "text-status-red" : "text-status-amber"}`}
        >
          {t(`master.problem.${p.code}`, (p.params ?? {}) as Record<string, unknown>)}
        </p>
      ))}
    </div>
  );
}

const labelClass = "block text-[11px] font-semibold uppercase tracking-wide text-brand-grey";
const controlClass = (bad: boolean) =>
  `mt-1 w-full rounded border px-2 py-1.5 text-sm focus:outline-none ${bad ? "border-status-red focus:border-status-red" : "border-brand-grey-light focus:border-brand-primary"}`;

export type Option = { value: string; label: string };

export function Field({
  spec,
  label,
  hint,
  value,
  problems,
  options,
  onChange,
}: {
  spec: FieldSpec;
  label: string;
  hint?: string;
  value: unknown;
  problems: Problem[];
  options?: Option[];
  onChange: (v: unknown) => void;
}) {
  const mine = forField(problems, spec.field);
  const bad = mine.some((p) => p.severity === "error");
  const asText = value == null ? "" : String(value);

  if (spec.kind === "check") {
    return (
      <label className="flex items-start gap-2 pt-5" data-master-field={spec.field}>
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          data-master-input={spec.field}
          className="mt-0.5 h-4 w-4 rounded border-brand-grey-light"
        />
        <span>
          <span className="text-sm font-medium text-brand-charcoal">{label}</span>
          {hint && <span className="mt-0.5 block text-[11px] text-brand-grey">{hint}</span>}
          <Problems problems={mine} />
        </span>
      </label>
    );
  }

  return (
    <label className={spec.span ? "sm:col-span-2 lg:col-span-3" : undefined} data-master-field={spec.field}>
      <span className={labelClass}>
        {label}
        {spec.required && <span className="ml-0.5 text-status-red">*</span>}
      </span>
      {spec.kind === "area" ? (
        <textarea
          value={asText}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          data-master-input={spec.field}
          className={controlClass(bad)}
        />
      ) : spec.kind === "pick" ? (
        <select
          value={asText}
          onChange={(e) => onChange(e.target.value)}
          data-master-input={spec.field}
          className={`${controlClass(bad)} bg-white`}
        >
          <option value="">—</option>
          {(options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={spec.kind === "money" || spec.kind === "number" ? "number" : spec.kind === "text" ? "text" : spec.kind}
          inputMode={spec.kind === "money" || spec.kind === "number" ? "decimal" : undefined}
          value={asText}
          onChange={(e) => onChange(e.target.value)}
          data-master-input={spec.field}
          className={controlClass(bad)}
        />
      )}
      {hint && <span className="mt-0.5 block text-[11px] text-brand-grey">{hint}</span>}
      <Problems problems={mine} />
    </label>
  );
}
